import * as yup from 'yup';
import type { PhoneNumberWithCountryCode } from '@gov-design-system-ce/react-form';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type {
	FormCheckboxProps,
	FormInputProps,
	FormPhoneNumberProps,
} from '@gov-nx/ui/types';
import type {
	CheckboxDefinition,
	InputDefinition,
	PhoneDefinition,
} from '@gov-nx/utils/common';
import {
	getCheckboxDefinition,
	useEmailShape,
	useInputDefinition,
	usePhoneDefinition,
	usePhoneShapeWithCountryCodes,
} from '@gov-nx/utils/common';

export interface ContactDetailsFormData {
	_useEmail?: boolean;
	_usePhone?: boolean;
	email?: string;
	phone?: PhoneNumberWithCountryCode;
}

export interface ContactDetailsFormFields {
	_useEmail: FormCheckboxProps;
	email: FormInputProps;
	_usePhone: FormCheckboxProps;
	phone: FormPhoneNumberProps;
}

export type ContactDetailsDefinition = [
	CheckboxDefinition,
	InputDefinition,
	CheckboxDefinition,
	PhoneDefinition,
];
export const useContactDetailsDefinition = ({
	isDisabled,
	strings,
}: {
	isDisabled: boolean;
	strings: {
		_useEmail: { label: string };
		_usePhone: { label: string };
		email: { label: string; placeholder: string };
		phone: { label: string; placeholder: string };
	};
}): ContactDetailsDefinition => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);

	const useEmail = getCheckboxDefinition({
		name: '_useEmail',
		isDisabled,
		label: strings._useEmail.label,
	});
	const usePhone = getCheckboxDefinition({
		name: '_usePhone',
		isDisabled,
		label: strings._usePhone.label,
	});

	const email = useInputDefinition({
		name: 'email',
		placeholder: strings.email.placeholder,
		label: strings.email.label,
		isEmail: true,
		isDisabled,
		isRequiredWhen: {
			fieldName: useEmail.field.field.name,
			is: true,
			errorMessage: t('validations.zadejte-kontaktni-email'),
		},
	});

	const phone = usePhoneDefinition({
		name: 'phone',
		placeholder: strings.phone.placeholder,
		label: '',
		isDisabled,
		isRequiredWhen: {
			fieldName: usePhone.field.field.name,
			is: true,
		},
	});

	return [useEmail, email, usePhone, phone];
};

export interface ContactDetailsFields {
	_useEmail: FormCheckboxProps['field'];
	email: FormInputProps['field'];
	_usePhone: FormCheckboxProps['field'];
	phone: FormInputProps['field'];
}

export const useContactDetailsFormDefinition = () => {
	const { getPhoneShape, getDefaultValue } = usePhoneShapeWithCountryCodes();
	const { getEmailShape } = useEmailShape();
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);

	return {
		shape: {
			_useEmail: yup.boolean(),
			_usePhone: yup.boolean(),
			email: getEmailShape().when('_useEmail', ([_useEmail], schema) => {
				if (_useEmail) {
					return schema.required(t('validations.zadejte-kontaktni-email'));
				}
				return schema.optional();
			}),
			phone: getPhoneShape().when('_usePhone', ([_usePhone], schema) => {
				if (_usePhone) {
					return getPhoneShape({ isRequired: true });
				}
				return schema.optional();
			}),
		},
		defaultValues: {
			_useEmail: false,
			email: undefined,
			_usePhone: false,
			phone: getDefaultValue(),
		},
		fields: (
			isProcessing: boolean,
			strings: {
				email: { label: string; placeholder: string };
				phone: { label: string; placeholder: string };
			}
		) => {
			const fields: ContactDetailsFields = {
				_useEmail: {
					name: '_useEmail',
					isDisabled: isProcessing,
					label: {
						children: strings.email.label,
					},
				},
				email: {
					name: 'email',
					isDisabled: isProcessing,
					placeholder: strings.email.placeholder,
				},
				_usePhone: {
					name: '_usePhone',
					isDisabled: isProcessing,
					label: {
						children: strings.phone.label,
					},
				},
				phone: {
					name: 'phone',
					isDisabled: isProcessing,
					placeholder: strings.phone.placeholder,
				},
			};

			return fields;
		},
	};
};
